/**
 * The events list and one event's page (Discord's "Events" modal), in one
 * dialog: a bottom sheet on a phone (§12.3). Each card shows the cover,
 * when (in the viewer's own time), where, the description, how many are
 * interested, and what you may do: Interested, Join a live channel event,
 * Start, and the "…" menu (Edit, Start, End, Cancel), which a long press
 * opens too. An event's page adds the interested people.
 */
import {
  GuildScheduledEventStatus as ST,
  type GuildScheduledEvent,
  type GuildScheduledEventUser,
  type Snowflake,
} from '@neosocial/protocol';
import { pushBack } from '@neosocial/ui/back';
import { readLayout } from '@neosocial/ui/layout';
import { useEffect, useId, useState } from 'react';
import type React from 'react';
import { useShallow } from 'zustand/react/shallow';
import { joinVoiceFromUi } from '../hub/voiceUi';
import { tr as translator, useT } from '../i18n';
import { assetUrl } from '../lib/config';
import { errorText, runAction } from '../mod/actions';
import { useData } from '../store/data';
import { displayName, useActiveGuildId } from '../store/selectors';
import { Avatar, Btn, EmptyState, Icon, IconBtn } from '../ui/kit';
import { confirmDialog, Dialog, toast, type MenuItem } from '../ui/overlay';
import { mayCreateAny, mayRunEvent } from './perms';
import { eventsApi, eventsOf, forgetEvent, rememberEvent, setInterested, useEvents } from './store';
import {
  closeEvents,
  openEditor,
  openEvent,
  openEvents,
  useEventsUi,
  whenText,
  whereOf,
  zoneName,
  type EventsView,
} from './ui';

/* ------------------------------------------------------------- actions */

async function changeStatus(e: GuildScheduledEvent, status: number): Promise<void> {
  const { t } = translator();
  if (status === ST.COMPLETED) {
    const ok = await confirmDialog({
      title: t('ev.end.title', { name: e.name }),
      body: t('ev.end.body'),
      confirmLabel: t('ev.end.go'),
      icon: 'calendar',
    });
    if (!ok) return;
  }
  const r = await runAction(() =>
    eventsApi.modify(e.guild_id, e.id, { status: status as GuildScheduledEvent['status'] }),
  );
  if (!r.ok) return;
  if (r.value.status === ST.ACTIVE || r.value.status === ST.SCHEDULED) rememberEvent(r.value);
  else forgetEvent(e.id);
  toast(t(status === ST.ACTIVE ? 'ev.started' : 'ev.ended'), { icon: 'calendar', tone: 'ok' });
}

/** Discord's "Cancel event": the event goes (DELETE). */
async function cancelEvent(e: GuildScheduledEvent): Promise<void> {
  const { t } = translator();
  const ok = await confirmDialog({
    title: t('ev.cancel.title', { name: e.name }),
    body: t('ev.cancel.body'),
    confirmLabel: t('ev.cancel.go'),
    danger: true,
    icon: 'trash',
  });
  if (!ok) return;
  const r = await runAction(() => eventsApi.remove(e.guild_id, e.id));
  if (!r.ok) return;
  forgetEvent(e.id);
  toast(t('ev.canceled'), { icon: 'calendar', tone: 'ok' });
  const v = useEventsUi.getState().view;
  if (v?.kind === 'event' && v.id === e.id) openEvents();
}

async function toggleInterested(e: GuildScheduledEvent, on: boolean): Promise<void> {
  try {
    await setInterested(e, on);
  } catch (err) {
    toast(errorText(err), { tone: 'danger', icon: 'alert' });
  }
}

function join(e: GuildScheduledEvent): void {
  if (!e.channel_id) return;
  closeEvents();
  void joinVoiceFromUi(e.channel_id);
}

/** The "…" menu (and a card's long press). */
export function eventMenuItems(
  t: (k: string, v?: Record<string, string | number>) => string,
  e: GuildScheduledEvent,
  o: { canRun: boolean; interested: boolean; from: 'list' | 'event' },
): MenuItem[] {
  const items: MenuItem[] = [
    {
      label: t('ev.interested'),
      icon: 'bell',
      checked: o.interested,
      toggle: true,
      run: () => void toggleInterested(e, !o.interested),
    },
  ];
  if (e.status === ST.ACTIVE && e.channel_id)
    items.push({ label: t('ev.join'), icon: 'volume', run: () => join(e) });
  if (o.canRun) {
    items.push({ sep: true });
    if (e.status === ST.SCHEDULED)
      items.push({
        label: t('ev.start'),
        icon: 'play',
        run: () => void changeStatus(e, ST.ACTIVE),
      });
    if (e.status === ST.ACTIVE)
      items.push({ label: t('ev.end'), icon: 'x', run: () => void changeStatus(e, ST.COMPLETED) });
    items.push({
      label: t('ev.edit'),
      icon: 'edit',
      run: () => openEditor(e.id, { back: o.from }),
    });
    if (e.status === ST.SCHEDULED)
      items.push({
        label: t('ev.cancel'),
        icon: 'trash',
        danger: true,
        run: () => void cancelEvent(e),
      });
  }
  return items;
}

/* --------------------------------------------------------------- a card */

function EventCard({ e, page }: { e: GuildScheduledEvent; page: boolean }) {
  const tr = useT();
  const { t } = tr;
  const interested = useEvents((s) => !!s.mine[e.id]);
  const canRun = useData((d) => mayRunEvent(d, e));
  const where = useData(useShallow((d) => whereOf(d, tr, e)));
  // the host's actions (End, Edit, Cancel) open in the card itself: the kit's
  // pop-up menus sit under dialogs, and this list is one (a sheet on a phone)
  const [more, setMore] = useState(false);
  const moreId = useId();
  const live = e.status === ST.ACTIVE;
  const n = e.user_count ?? 0;
  const hostItems = canRun
    ? eventMenuItems(t, e, { canRun, interested, from: page ? 'event' : 'list' }).filter(
        (i) =>
          i.label &&
          i.label !== t('ev.interested') &&
          i.label !== t('ev.join') &&
          i.label !== t('ev.start'),
      )
    : [];
  return (
    <article
      className="ev-card"
      data-ev={e.id}
      data-live={live || undefined}
      data-menu={more || undefined}
      aria-label={e.name}
      onContextMenu={(ev: React.MouseEvent) => {
        // a long press (or a right click) shows the host's actions
        if (!hostItems.length || (ev.target as HTMLElement).closest('a, input, textarea')) return;
        ev.preventDefault();
        setMore(true);
      }}
    >
      {e.image ? (
        <img className="ev-cover" src={assetUrl(e.image) ?? undefined} alt="" loading="lazy" />
      ) : null}
      <div className="ev-body">
        <p className="ev-when" data-live={live || undefined}>
          {live ? <i className="ev-dot" aria-hidden="true" /> : <Icon name="calendar" />}
          <span>
            {live ? <b>{t('ev.live')}</b> : null}
            {live ? ' · ' : ''}
            {whenText(tr, e)}
          </span>
        </p>
        {page ? (
          <h3 className="ev-name" dir="auto">
            {e.name}
          </h3>
        ) : (
          <h3 className="ev-name">
            <button type="button" className="ev-open" dir="auto" onClick={() => openEvent(e.id)}>
              {e.name}
            </button>
          </h3>
        )}
        <p className="ev-where">
          <Icon name={where.icon} />
          <bdi dir="auto">{where.label}</bdi>
        </p>
        {e.description ? (
          <p className="ev-desc" dir="auto" data-clamp={!page || undefined}>
            {e.description}
          </p>
        ) : null}
        {page && e.creator ? (
          <p className="ev-by">
            <Avatar user={e.creator} size={20} />
            <span>{t('ev.by', { name: displayName(e.creator) })}</span>
          </p>
        ) : null}
        <div className="ev-foot">
          <span className="ev-count">
            <Icon name="users" />
            {t('ev.interestedCount', { n })}
          </span>
          <span className="ev-actions">
            {live && e.channel_id ? (
              <Btn size="sm" kind="mode" icon="volume" onClick={() => join(e)}>
                {t('ev.join')}
              </Btn>
            ) : null}
            {canRun && e.status === ST.SCHEDULED ? (
              <Btn
                size="sm"
                kind="secondary"
                icon="play"
                onClick={() => void changeStatus(e, ST.ACTIVE)}
              >
                {t('ev.startShort')}
              </Btn>
            ) : null}
            <Btn
              size="sm"
              kind={interested ? 'primary' : 'secondary'}
              icon={interested ? 'check' : 'bell'}
              aria-pressed={interested}
              aria-label={t(interested ? 'ev.interestedA11yOn' : 'ev.interestedA11yOff', {
                name: e.name,
              })}
              onClick={() => void toggleInterested(e, !interested)}
            >
              {t('ev.interested')}
            </Btn>
          </span>
          {/* beside the actions; on a phone it moves up beside the count, so
              the buttons keep a row of their own (QA D2, D8) */}
          {hostItems.length ? (
            <IconBtn
              icon="more"
              className="ev-more-btn"
              label={t('ev.more', { name: e.name })}
              aria-expanded={more}
              aria-controls={moreId}
              onClick={() => setMore(!more)}
            />
          ) : null}
        </div>
        {more && hostItems.length ? (
          <div
            className="ev-more"
            id={moreId}
            role="group"
            aria-label={t('ev.more', { name: e.name })}
          >
            {hostItems.map((i) => (
              <Btn
                key={i.label}
                size="sm"
                kind={i.danger ? 'danger' : 'secondary'}
                icon={i.icon}
                onClick={() => {
                  setMore(false);
                  i.run?.();
                }}
              >
                {i.label}
              </Btn>
            ))}
          </div>
        ) : null}
      </div>
    </article>
  );
}

/* --------------------------------------------------- interested people */

function People({ e }: { e: GuildScheduledEvent }) {
  const { t } = useT();
  const [rows, setRows] = useState<GuildScheduledEventUser[] | null>(null);
  const [more, setMore] = useState(false);
  const count = e.user_count ?? 0;
  useEffect(() => {
    let live = true;
    eventsApi
      .users(e.guild_id, e.id)
      .then((r) => {
        if (!live) return;
        setRows(r);
        setMore(r.length >= 100);
      })
      .catch(() => live && setRows([]));
    return () => {
      live = false;
    };
    // the list follows the count (someone came or went)
  }, [e.guild_id, e.id, count]);
  const loadMore = () => {
    const last = rows?.[rows.length - 1]?.user.id;
    if (!last) return;
    void eventsApi.users(e.guild_id, e.id, last).then((r) => {
      setRows((old) => [...(old ?? []), ...r]);
      setMore(r.length >= 100);
    });
  };
  return (
    <section className="ev-people" aria-label={t('ev.people')}>
      <h4 className="ns-label">{t('ev.people')}</h4>
      {rows === null ? null : rows.length === 0 ? (
        <p className="ns-muted">{t('ev.people.none')}</p>
      ) : (
        <ul className="ev-people-list">
          {rows.map((r) => (
            <li key={r.user.id} className="ev-person">
              <Avatar user={r.user} size={28} />
              <span className="ev-person-n" dir="auto">
                {displayName(r.user, r.member)}
              </span>
            </li>
          ))}
        </ul>
      )}
      {more ? (
        <Btn size="xs" kind="quiet" onClick={loadMore}>
          {t('ev.people.more')}
        </Btn>
      ) : null}
    </section>
  );
}

/* -------------------------------------------------------------- dialog */

export function EventsDialog({ view }: { view: Extract<EventsView, { kind: 'list' | 'event' }> }) {
  const { t } = useT();
  const gid = useActiveGuildId();
  const events = useEvents(useShallow((s) => eventsOf(s, gid)));
  const loaded = useEvents((s) => !!gid && !!s.loaded[gid]);
  const canCreate = useData((d) => mayCreateAny(d, gid));
  const id: Snowflake | null = view.kind === 'event' ? view.id : null;
  const shown = id ? (events.find((e) => e.id === id) ?? null) : null;
  // an event that finished or went while open: back to the list
  useEffect(() => {
    if (id && loaded && !shown) openEvents();
  }, [id, loaded, shown]);
  // Back (a phone's, the browser's) goes from an event to the list
  useEffect(() => {
    if (!id || readLayout().layout === 'desktop') return;
    return pushBack(() => openEvents());
  }, [id]);
  return (
    <Dialog
      title={t('ev.title')}
      icon="calendar"
      tone="mode"
      size="md"
      cls="ev-dlg"
      onClose={closeEvents}
      actions={
        shown ? (
          <Btn kind="ghost" size="sm" icon="chev-left" onClick={openEvents}>
            {t('ev.back')}
          </Btn>
        ) : canCreate ? (
          <Btn
            kind="primary"
            size="sm"
            icon="plus"
            onClick={() => openEditor(null, { back: 'list' })}
          >
            {t('ev.create')}
          </Btn>
        ) : undefined
      }
    >
      {shown ? (
        <div className="ev-page">
          <EventCard e={shown} page />
          <People e={shown} />
        </div>
      ) : events.length ? (
        <>
          <ul className="ev-list">
            {events.map((e) => (
              <li key={e.id}>
                <EventCard e={e} page={false} />
              </li>
            ))}
          </ul>
          <p className="ev-tz ns-muted">{t('ev.tz', { tz: zoneName() })}</p>
        </>
      ) : (
        <EmptyState
          icon="calendar"
          title={t('ev.empty.title')}
          hint={t(canCreate ? 'ev.empty.body' : 'ev.empty.member')}
        />
      )}
    </Dialog>
  );
}
